// AdminAuthContext.jsx - Authentification admin avec Firebase Auth
import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, getFirebaseToken } from '../firebase';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [firebaseToken, setFirebaseToken] = useState(null);

  // Configuration admin (emails autorisés pour l'administration)
  const ADMIN_EMAILS = [
    process.env.REACT_APP_ADMIN_EMAIL || 'admin@aladinn-edu.com',
    'admin@aladinn.com',
    // Ajoutez d'autres emails admin si nécessaire
  ];

  // Configuration des identifiants par défaut pour demo
  const DEMO_CREDENTIALS = {
    email: process.env.REACT_APP_ADMIN_EMAIL || 'admin@aladinn-edu.com',
    password: process.env.REACT_APP_ADMIN_PASSWORD || 'aladinn2024'
  };

  useEffect(() => {
    // Écouter les changements d'état d'authentification Firebase
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      
      if (user) {
        // Vérifier si l'utilisateur est un admin autorisé
        const isAdmin = ADMIN_EMAILS.includes(user.email);
        
        if (isAdmin) {
          try {
            // Récupérer le token Firebase
            const token = await getFirebaseToken();
            
            setUser(user);
            setFirebaseToken(token);
            setIsAuthenticated(true);
            
            // Stocker le token pour les autres services
            if (token) {
              localStorage.setItem('authToken', token);
              sessionStorage.setItem('authToken', token);
              localStorage.setItem('admin_session', JSON.stringify({
                email: user.email,
                loginTime: Date.now(),
                tokenExpiry: Date.now() + (60 * 60 * 1000) // 1 heure
              }));
            }
            
            console.log('✅ Admin authentifié via Firebase:', user.email);
          } catch (error) {
            console.error('❌ Erreur récupération token admin:', error);
            setIsAuthenticated(false);
          }
        } else {
          console.warn('⚠️ Utilisateur non autorisé pour l\'admin:', user.email);
          await signOut(auth);
          setIsAuthenticated(false);
        }
      } else {
        // Utilisateur déconnecté
        setUser(null);
        setFirebaseToken(null);
        setIsAuthenticated(false);
        
        // Nettoyer les tokens stockés
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        localStorage.removeItem('admin_session');
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Connexion admin via Firebase Auth
  const login = async (email, password) => {
    try {
      setIsLoading(true);
      
      // Vérifier si c'est un email admin autorisé
      if (!ADMIN_EMAILS.includes(email)) {
        return { 
          success: false, 
          error: 'Email non autorisé pour l\'administration' 
        };
      }

      // Authentification via Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Le reste sera géré par onAuthStateChanged
      console.log('✅ Connexion admin réussie:', user.email);
      
      return { success: true, user };
      
    } catch (error) {
      console.error('❌ Erreur connexion admin:', error);
      
      let errorMessage = 'Erreur de connexion';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte admin trouvé avec cet email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives. Réessayez plus tard';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
      console.log('✅ Admin déconnecté');
    } catch (error) {
      console.error('❌ Erreur déconnexion:', error);
    }
  };

  // Rafraîchir le token Firebase
  const refreshToken = async () => {
    try {
      if (user) {
        const newToken = await user.getIdToken(true); // forceRefresh = true
        setFirebaseToken(newToken);
        localStorage.setItem('authToken', newToken);
        sessionStorage.setItem('authToken', newToken);
        return newToken;
      }
      return null;
    } catch (error) {
      console.error('❌ Erreur rafraîchissement token:', error);
      return null;
    }
  };

  // Vérifier la validité du token
  const validateToken = async () => {
    try {
      if (!user) return false;
      
      const token = await getFirebaseToken();
      return !!token;
    } catch (error) {
      console.error('❌ Token invalide:', error);
      return false;
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    firebaseToken,
    login,
    logout,
    refreshToken,
    validateToken,
    isAdmin: isAuthenticated && user && ADMIN_EMAILS.includes(user.email),
    adminEmails: ADMIN_EMAILS,
    demoCredentials: DEMO_CREDENTIALS
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Composant de protection des routes admin
export const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isAdmin } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification des permissions admin...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <AdminLoginPage />;
  }

  return children;
};

// Page de connexion admin
const AdminLoginPage = () => {
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: '' 
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { login, demoCredentials } = useAdminAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(credentials.email, credentials.password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const fillDemoCredentials = () => {
    setCredentials({
      email: demoCredentials.email,
      password: demoCredentials.password
    });
    setShowDemo(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Administration Aladinn
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Authentification Firebase pour accès admin
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email administrateur
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email administrateur"
                value={credentials.email}
                onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                'Se connecter avec Firebase'
              )}
            </button>
          </div>
          
          {/* Section Demo */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowDemo(!showDemo)}
              className="text-sm text-blue-600 hover:text-blue-500 underline"
            >
              {showDemo ? 'Masquer' : 'Afficher'} les identifiants de démo
            </button>
            
            {showDemo && (
              <div className="mt-3 text-xs text-gray-500 bg-yellow-50 p-3 rounded border">
                <div className="mb-2">
                  <strong>Identifiants de démo :</strong>
                </div>
                <div className="mb-1">Email : {demoCredentials.email}</div>
                <div className="mb-3">Mot de passe : {demoCredentials.password}</div>
                <button
                  type="button"
                  onClick={fillDemoCredentials}
                  className="bg-yellow-200 hover:bg-yellow-300 text-yellow-800 px-3 py-1 rounded text-xs"
                >
                  Remplir automatiquement
                </button>
                <div className="mt-2 text-xs text-yellow-700">
                  ⚠️ Assurez-vous que ce compte existe dans Firebase Auth
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAuthProvider;